import {
  IconButton,
  Box,
  ThemeProvider
} from '@mui/material';
import { Dialog } from '@cw-ui/feedback-components';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { formatMessage, hideDialog, removeModuleCache, removeScreenData, setModuleCache, Store } from 'cw-ui-core';
import Locale from 'src/Locale';
import { getTheme } from '@cw-ui/asio-neon-themes';
import { Stepper } from '@cw-ui/navigation-components';
import AsioSuccessStep from './AsioSuccessStep';
import { Button } from '@cw-ui/form-components';
import AsioFirstStepContainer from './AsioFirstStepContainer';
import AsioFailureStep from './AsioFailureStep';
import AsioSecondStepContainer from './AsioSecondStepContainer';
import { ASIO_ENABLEMENT_DIALOG_ID } from './AsioEnablementDialogContainer';
import Loader from 'src/LoaderCustom';
import { getProducts } from 'src/services/Products/ProductsService';
import { getApplicationRoles } from 'src/services/User/UserService';
import BrightGaugeConfirmationStepContainer from './BrightGaugeConfirmationStepContainer';
import CustomizedSpinner from './CustomizedSpinner';
import { getWhatsNextData } from 'src/services/AsioService';

const AsioEnablementDialog = props => {
  const theme = getTheme('light');
  const [activeStep, setActiveStep] = useState(
    props.asioDialogStep === 2 || props.asioDialogStep === 3
      ? 0
      : props.asioDialogStep
        ? props.asioDialogStep
        : 0
  );
  const [isAgreed, setIsAgreed] = useState(false);
  const [brightGaugeValidationErrors, setBrightGaugeValidationErrors] = useState(false);
  const [stepperWidth, setStepperWidth] = useState('50%');

  const [isLoading, setLoading] = useState(false);
  const [linkPSAInstanceResult, setLinkPSAInstanceResult] = useState('');
  const showStepper = false;

  const handleAgreementChange = isChecked => {
    setIsAgreed(isChecked);
  };

  const handleBrightGaugeValidationErrors = value => {
    setBrightGaugeValidationErrors(value);
  };

  useEffect(() => {
    setActiveStep(
      props.asioDialogStep === 2 || props.asioDialogStep === 3
        ? 0
        : props.asioDialogStep
          ? props.asioDialogStep
          : 0
    );
  }, [props.asioDialogStep]);

  const [steps, setNewStep] = useState([
    {
      step: 0,
      title: 'Confirm Account Details',
      label: '',
      content: (
        <AsioFirstStepContainer
          onAgreementChange={handleAgreementChange}
        />
      )
    },
    {
      step: 1,
      title: 'Link Classic PSA Instances',
      label: '',
      content: (
        <AsioSecondStepContainer />
      )
    }
  ]);

  useEffect(() => {
    if (props.isAsioFailureDialogOpen) {
      setNewStep(prevSteps => [
        ...prevSteps,
        {
          step: 2,
          title: 'Error',
          label: '',
          error: true,
          content: <AsioFailureStep data={props.asioDecommissioningResult} />,
          completed: false
        }
      ]);
      setActiveStep(prevActiveStep => prevActiveStep + 2);
      setLinkPSAInstanceResult(props.asioDecommissioningResult);
    }

    // eslint-disable-next-line
  },[]);

  const scrollDialogToTop = () => {
    const paperRef = document?.querySelector('.MuiDialogContent-root');
    if (paperRef) {
      paperRef.scrollTop = 0;
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      setLoading(true);
    }

    const result = await props.validateAsioProvisioningData();
    scrollDialogToTop();

    if (result) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    } else {
      setActiveStep(prevActiveStep => prevActiveStep);
    }

    if (activeStep === 0) {
      setLoading(false);
    }
  };

  const onProceedButtonClick = async () => {
    setLoading(true);
    const validationResult = await props.validateBrightGaugeDetails();
    if (!validationResult) {
      setActiveStep(prevActiveStep => prevActiveStep);
    } else {
      //call the package provisioning here.
      const result = await props.provisionAsioAndBrightGauge();
      const isSuccess =
        result?.isSuccess &&
        result?.response?.packageId &&
        ['success', 'partial'].includes(result?.response?.status);

      setLinkPSAInstanceResult(result);

      setNewStep(prevSteps => [
        ...prevSteps,
        {
          step: 3,
          title: isSuccess ? 'Success' : 'Error',
          label: '',
          content: isSuccess
        ? <AsioSuccessStep showNewTitle={!props.isFullAccountUpgrade} isFullAccountUpgrade={props.isFullAccountUpgrade} />
        : <AsioFailureStep data={result} />,
          completed: !!isSuccess,
          error: !isSuccess
        }
      ]);
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setStepperWidth('95%');
    }

    setLoading(false);
  };

  const linkPSAInstanaceClick = async () => {
    setLoading(true);

    const result = await props.onLinkPSAInstanceClick();
    scrollDialogToTop();

    setLinkPSAInstanceResult(result);
    if (result === true) {
      // Only show BrightGauge Details step
      setNewStep(prevSteps => [
        ...prevSteps,
        {
          step: 2,
          title: 'BrightGauge Details',
          label: '',
          content: (
            <BrightGaugeConfirmationStepContainer
              validationErrors={handleBrightGaugeValidationErrors}
            />
          )
        }
      ]);
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setStepperWidth('75%');
    } else if (result?.isSuccess && (result?.response?.instanceId || (result?.response?.packageId && ["success","partial"].includes(result?.response?.status)))) {
      // Show Success directly at step 2
      setNewStep(prevSteps => [
        ...prevSteps,
        {
          step: 2,
          title: 'Success',
          label: '',
          content: <AsioSuccessStep isFullAccountUpgrade={props.isFullAccountUpgrade} isAutomateInAsioUpgrade={props.isAutomateInAsioUpgrade} selectedFeature={props.selectedFeature} />,
          completed: true
        }
      ]);
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setStepperWidth('75%');
    } else {
      // Show Error directly at step 2
      setNewStep(prevSteps => [
        ...prevSteps,
        {
          step: 2,
          title: 'Error',
          label: '',
          error: true,
          content: <AsioFailureStep data={result} />,
          completed: false
        }
      ]);
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setStepperWidth('75%');
    }

    setLoading(false);
  };

  const handlePrevious = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
    if (props.isBrightGaugeStepper) {
      props.clearBrightGaugeValidations();
      setBrightGaugeValidationErrors(false);
      setNewStep(prevSteps => prevSteps.filter(step => step.step !== 2));
      setStepperWidth('50%');
    }

    setIsAgreed(false);
  };

  const isWhatsNext = (activeStep === 2 && props.isSuccess) || (activeStep === 3 && props.isSuccess);
  const isNeedSupport = activeStep === 0 || activeStep === 1 || (activeStep === 2 && props.isBrightGaugeStepper);
  const isStep1PreviousBlocked = activeStep === 1 && (
    (props.isAutomateInAsioUpgrade && props.selectedFeature !== 'automate') ||
    (props.isFullAccountUpgrade && props.selectedFeature !== 'psa') 
  );

  const handleonButtonClick = async () => {
    if (!isWhatsNext) return;
    setLoading(true);
    const dispatch = Store().dispatch;
    await getApplicationRoles('platform');
    await getProducts(true);
    if (props.isFullAccountUpgrade || props.isAutomateInAsioUpgrade) {
      await getWhatsNextData();
    }

    dispatch(setModuleCache('isWhatsNextPage', true));
    dispatch(removeModuleCache('isFullAccountUpgrade'));
    dispatch(removeModuleCache('isAutomateInAsioUpgrade'));
    dispatch(removeModuleCache('isDefaultProvisioning'));
    dispatch(removeModuleCache('selectedFeature'));
    dispatch(removeScreenData('isSuccess'));
    dispatch(hideDialog(ASIO_ENABLEMENT_DIALOG_ID));
    setLoading(false);
  };

  const getTitle = () => {
    if (isLoading) return <Loader />;

    switch (activeStep) {
      case 0:
        return formatMessage(Locale.confirm_account);
      case 1:
        return props.manageProducts.length > 0 || props.automateProducts.length > 0
          ? formatMessage(Locale.proceed)
          : formatMessage(Locale.activate_asio);
      case 2:
        return !props.isSuccess && !props.isBrightGaugeStepper ? formatMessage(Locale.view_case) : props.isBrightGaugeStepper ? formatMessage(Locale.create_account) : null;
      case 3:
        return activeStep === 3 && !props.isSuccess ? formatMessage(Locale.view_case) : null;
      default:
        return null;
    }
  };

  function getWhatNextTitle() {
    if (isLoading) return <Loader />;
    return formatMessage(Locale.whats_next);
  }

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open
        aria-labelledby={props.isSuccess ? '' : 'asio-enablement-dialog'}
        aria-describedby="asio-enablement-dialog-description"
        sx={{
          '& .MuiDialog-paper': {
            height: '90vh',
            width: '70vw',
            maxWidth: '1200px'
          }
        }}
      >
        {!props.isSuccess && (
          <Dialog.Title id="asio-enablement-dialog">
            {activeStep === 2 && !props.isBrightGaugeStepper
              ? formatMessage(Locale.how_can_we_help)
              : formatMessage(Locale.lets_get_started)}
          </Dialog.Title>
        )}
        {!props.isSuccess && (
          <IconButton
            aria-label="close"
            onClick={props.onCloseIconClick}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.icon.iconPrimary
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Dialog.DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
          {showStepper && (
            <Stepper
              sx={{ width: stepperWidth, padding: '8px' }}
              activeStep={activeStep}
              orientation="horizontal"
              variant="dots"
              steps={steps}
            ></Stepper>
          )}
          {isLoading && props.isBrightGaugeStepper && activeStep === 2 && <CustomizedSpinner />}
          {steps[activeStep]?.content}
        </Dialog.DialogContent>
        <Dialog.DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          {!isWhatsNext && (
            <Button
              variant={isStep1PreviousBlocked ? '' : 'outlined'}
              size="medium"
              color={isStep1PreviousBlocked ? '' : 'tertiary'}
              disabled={isStep1PreviousBlocked}
              sx={{
                textTransform: 'unset !important',
                fontWeight: '600',
                fontSize: '14px',
                color: '#2E3F80'
              }}
              onClick={
                activeStep === 0
                  ? props.onCloseIconClick
                  : (activeStep === 1 && ((props.isAutomateInAsioUpgrade && props.selectedFeature === 'automate') || (props.isFullAccountUpgrade && props.selectedFeature === 'psa') || props.isDefaultProvisioning))
                  ? handlePrevious
                  : (activeStep === 2 &&
                      !props.isSuccess &&
                      !props.isBrightGaugeStepper) ||
                    (activeStep === 3 &&
                      !props.isSuccess)
                  ? () => props.onNeedSupportClick(activeStep)
                  : activeStep === 2 && props.isBrightGaugeStepper
                  ? handlePrevious
                  : null
              }
            >
              {activeStep === 0
                ? formatMessage(Locale.cancel)
                : (activeStep === 1 && ((props.isAutomateInAsioUpgrade && props.selectedFeature === 'automate') || (props.isFullAccountUpgrade && props.selectedFeature === 'psa') || props.isDefaultProvisioning))
                ? formatMessage(Locale.previous)
                : (activeStep === 2 &&
                    !props.isSuccess &&
                    !props.isBrightGaugeStepper) ||
                  (activeStep === 3 &&
                    !props.isSuccess)
                ? formatMessage(Locale.need_support_text)
                : activeStep === 2 && props.isBrightGaugeStepper
                ? formatMessage(Locale.previous)
                : null}
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            {(activeStep === 2 && props.isSuccess) ||
            (activeStep === 3 && props.isSuccess) ||
            (activeStep === 2 && props.isBrightGaugeStepper) ||
            activeStep === 0 ||
            activeStep === 1 ? (
              <Button
                variant="outlined"
                size="medium"
                color="secondary"
                onClick={
                  isNeedSupport
                    ? () => props.onNeedSupportClick(activeStep, (props.isFullAccountUpgrade || props.isAutomateInAsioUpgrade || props.isDefaultProvisioning) && !(activeStep === 2 && props.isBrightGaugeStepper))
                    : handleonButtonClick
                }
                sx={{
                  textTransform: 'unset !important',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {isNeedSupport
                  ? formatMessage(Locale.need_support)
                  : isWhatsNext
                  ? getWhatNextTitle()
                  : null}
              </Button>
            ) : null}
            {(activeStep === 2 &&
              !props.isSuccess &&
              !props.isBrightGaugeStepper) ||
            (activeStep === 3 &&
              !props.isSuccess) ||
            (activeStep === 2 && props.isBrightGaugeStepper) ||
            activeStep === 0 ||
            activeStep === 1 ? (
              <Button
                variant="contained"
                size="medium"
                color="primary"
                disabled={activeStep === 0 ? !isAgreed : props.isBrightGaugeStepper ? !!brightGaugeValidationErrors : false}
                onClick={() => {
                  if (activeStep === 1) {
                    linkPSAInstanaceClick();
                  } else if (
                    (activeStep === 2 &&
                      !props.isSuccess &&
                      !props.isBrightGaugeStepper) ||
                    (activeStep === 3 &&
                      !props.isSuccess) //add one more condition in this.
                  ) {
                    props.onViewCaseClick(linkPSAInstanceResult);
                  } else if (activeStep === 2 && props.isBrightGaugeStepper) {
                    onProceedButtonClick();
                  } else {
                    handleNext();
                  }
                }}
                sx={{
                  textTransform: 'unset !important',
                  fontSize: '14px',
                  backgroundColor: isLoading ? 'white' : ''
                }}
              >
                {getTitle()}
              </Button>
            ) : null}
          </Box>
        </Dialog.DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default injectIntl(AsioEnablementDialog);
